import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { useUserProfile } from './hooks/useUserProfile'
import { EmailPromptModal } from './components/EmailPromptModal'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { Applications } from './pages/Applications'
import { ApplicationForm } from './pages/ApplicationForm'
import { Organizations } from './pages/platform/Organizations'
import { OrganizationForm } from './pages/platform/OrganizationForm'
import { SsoConnectionForm } from './pages/platform/SsoConnectionForm'
import { Statistics } from './pages/Statistics'
import { DocsHome } from './pages/docs/DocsHome'
import { DocsSdk } from './pages/docs/DocsSdk'
import { DocsIntegration } from './pages/docs/DocsIntegration'
import { DocsApi } from './pages/docs/DocsApi'
import { AuthCallback } from './pages/AuthCallback'
import { SsoCallback } from './pages/SsoCallback'
import { OAuthAuthorize } from './pages/OAuthAuthorize'
import { LinkProvider } from './pages/LinkProvider'
import { InviteAccept } from './pages/InviteAccept'
import { UserLayout } from './components/UserLayout'
import { OrgLayout } from './components/OrgLayout'
import { Home } from './pages/user/Home'
import { Account } from './pages/user/Account'
import { Privacy } from './pages/user/Privacy'
import { Security } from './pages/user/Security'
import { Activity } from './pages/user/Activity'
import { Subscriptions } from './pages/user/Subscriptions'
import { Transactions } from './pages/user/Transactions'
import { Users } from './pages/user/Users'
import { UserDetail } from './pages/user/UserDetail'
import { MyOrganizations } from './pages/user/MyOrganizations'
import { LinkedAccounts } from './pages/user/LinkedAccounts'
import { OrgDashboard } from './pages/org/OrgDashboard'
import { OrgDomains } from './pages/org/OrgDomains'
import { OrgSso } from './pages/org/OrgSso'
import { OrgSsoForm } from './pages/org/OrgSsoForm'
import { OrgMembers } from './pages/org/OrgMembers'
import { OrgSettings } from './pages/org/OrgSettings'
import { OrgApps } from './pages/org/OrgApps'
import { OrgAppDetail } from './pages/org/OrgAppDetail'
import { OrgAnalytics } from './pages/org/OrgAnalytics'
import { OrgAuditLogs } from './pages/org/OrgAuditLogs'
import { OrgMemberDetail } from './pages/org/OrgMemberDetail'
import { Terms } from './pages/Terms'
import { PrivacyPolicy } from './pages/PrivacyPolicy'
import { Spinner } from 'wss3-forge'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-primary)'
      }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, mfaPending } = useAuth()

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-primary)'
      }}>
        <Spinner size="lg" />
      </div>
    )
  }

  // Don't redirect if MFA verification is pending
  // Don't redirect if there are app params (SSO flow will handle it)
  const urlParams = new URLSearchParams(window.location.search)
  const hasAppParams = urlParams.has('app') || urlParams.has('redirect_uri')

  if (user && !mfaPending && !hasAppParams) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function WebbaMemberRoute() {
  const { profile, loading } = useUserProfile()

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-primary)'
      }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (!profile?.is_webba_member) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

function AppRoutes() {
  return (
    <Routes>
      {/* All routes under UserLayout */}
      <Route
        element={
          <ProtectedRoute>
            <UserLayout />
          </ProtectedRoute>
        }
      >
        {/* User routes */}
        <Route index element={<Home />} />
        <Route path="account" element={<Account />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="security" element={<Security />} />
        <Route path="linked-accounts" element={<LinkedAccounts />} />
        <Route path="activity" element={<Activity />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="organizations" element={<MyOrganizations />} />
        <Route path="users" element={<Users />} />
        <Route path="users/:userId" element={<UserDetail />} />

        {/* Platform routes (Webba members only) */}
        <Route path="platform" element={<WebbaMemberRoute />}>
          <Route index element={<Navigate to="/platform/applications" replace />} />
          <Route path="applications" element={<Applications />} />
          <Route path="applications/new" element={<ApplicationForm />} />
          <Route path="applications/:id" element={<ApplicationForm />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="organizations/new" element={<OrganizationForm />} />
          <Route path="organizations/:id" element={<OrganizationForm />} />
          <Route path="organizations/:orgId/sso/new" element={<SsoConnectionForm />} />
          <Route path="organizations/:orgId/sso/:connectionId" element={<SsoConnectionForm />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="docs" element={<DocsHome />} />
          <Route path="docs/sdk" element={<DocsSdk />} />
          <Route path="docs/integration" element={<DocsIntegration />} />
          <Route path="docs/api" element={<DocsApi />} />
        </Route>
      </Route>

      {/* Auth routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/sso/callback" element={<SsoCallback />} />
      <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
      <Route
        path="/link-provider"
        element={
          <ProtectedRoute>
            <LinkProvider />
          </ProtectedRoute>
        }
      />

      {/* Invitation acceptance */}
      <Route
        path="/invite/:token"
        element={
          <ProtectedRoute>
            <InviteAccept />
          </ProtectedRoute>
        }
      />

      {/* Organization Admin Portal */}
      <Route
        path="/org/:slug"
        element={
          <ProtectedRoute>
            <OrgLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OrgDashboard />} />
        <Route path="apps" element={<OrgApps />} />
        <Route path="apps/:appId" element={<OrgAppDetail />} />
        <Route path="domains" element={<OrgDomains />} />
        <Route path="sso" element={<OrgSso />} />
        <Route path="sso/new" element={<OrgSsoForm />} />
        <Route path="sso/:connectionId" element={<OrgSsoForm />} />
        <Route path="members" element={<OrgMembers />} />
        <Route path="members/:memberId" element={<OrgMemberDetail />} />
        <Route path="analytics" element={<OrgAnalytics />} />
        <Route path="audit" element={<OrgAuditLogs />} />
        <Route path="settings" element={<OrgSettings />} />
      </Route>

      {/* Public legal pages */}
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <EmailPromptModal />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
